import api from './api';

const handleApiError = (error, defaultMessage = 'An error occurred') => {
  console.error('API Error:', error);
  
  if (error.response?.data?.error) {
    throw new Error(error.response.data.error);
  } else if (error.response?.data?.message) {
    throw new Error(error.response.data.message);
  } else if (error.response) {
    throw new Error(error.response.statusText || defaultMessage);
  } else if (error.request) {
    throw new Error('Network error: Unable to connect to server');
  } else {
    throw new Error(error.message || defaultMessage);
  }
};

// Keep existing functions for backward compatibility
export const login = async (credentials) => {
  try {
    // Only send email and password, not role
    const loginData = {
      email: credentials.email,
      password: credentials.password
    };

    console.log('🔄 Sending login request:', loginData);

    const response = await api.post('/auth/login', loginData);
    
    if (response.data.success && response.data.token) {
      // Store token and user data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Set default authorization header for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    }
    
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Login failed');
  }
};

export const register = async (userData) => {
  try {
    const response = await api.post('/auth/register', userData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Registration failed');
  }
};

export const registerRadiologist = async (radiologistData) => {
  try {
    const response = await api.post('/auth/register-radiologist', radiologistData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Radiologist registration failed');
  }
};

/**
 * Get radiologists (admin only)
 */
export const getRadiologists = async (params = {}) => {
  try {
    const response = await api.get('/auth/radiologists', { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch radiologists');
  }
};

/**
 * Delete radiologist (admin only)
 */
export const deleteRadiologist = async (radiologistId) => {
  try {
    const response = await api.delete(`/auth/radiologists/${radiologistId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to delete radiologist');
  }
};

/**
 * Send notification to radiologist (admin only)
 */
export const notifyRadiologist = async (radiologistId, data) => {
  try {
    const response = await api.post(`/auth/radiologists/${radiologistId}/notify`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to notify radiologist');
  }
};

/**
 * Get radiologist statistics
 */
export const getRadiologistStats = async (radiologistId) => {
  try {
    const response = await api.get(`/auth/radiologists/${radiologistId}/stats`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch radiologist statistics');
  }
};

// NEW ENHANCED FUNCTIONS

/**
 * Get current user information
 */
export const getCurrentUser = async () => {
  try {
    const response = await api.get('/auth/me');
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch user information');
  }
};

/**
 * Change password
 */
export const changePassword = async (passwordData) => {
  try {
    const response = await api.post('/auth/change-password', passwordData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Password change failed');
  }
};

/**
 * Logout user
 */
export const logout = () => {
  // Remove token from localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  
  // Remove authorization header from API
  delete api.defaults.headers.common['Authorization'];
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;

  // Optional: Add token expiration check here
  // You can decode the JWT and check expiration time
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    if (payload.exp && payload.exp < currentTime) {
      logout(); // Token expired, logout user
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error checking token validity:', error);
    return false;
  }
};

/**
 * Get stored user data
 */
export const getStoredUser = () => {
  try {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing stored user data:', error);
    return null;
  }
};

/**
 * Update user profile
 */
export const updateProfile = async (profileData) => {
  try {
    const response = await api.put('/auth/profile', profileData);
    
    // Update stored user data if successful
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Profile update failed');
  }
};

/**
 * Request password reset
 */
export const requestPasswordReset = async (email) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Password reset request failed');
  }
};

/**
 * Reset password with token
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await api.post('/auth/reset-password', {
      token,
      newPassword
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Password reset failed');
  }
};

/**
 * Verify email with token
 */
export const verifyEmail = async (token) => {
  try {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Email verification failed');
  }
};

/**
 * Resend email verification
 */
export const resendVerification = async (email) => {
  try {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to resend verification email');
  }
};

export default {
  login,
  register,
  registerRadiologist,
  getRadiologists,
  deleteRadiologist,
  notifyRadiologist,
  getRadiologistStats,
  getCurrentUser,
  changePassword,
  logout,
  isAuthenticated,
  getStoredUser,
  updateProfile,
  requestPasswordReset,
  resetPassword,
  verifyEmail,
  resendVerification
};
